import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/types/database';
import { ShieldX } from 'lucide-react';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  allowedRoles,
  children,
  fallback,
}) => {
  const { role, loading } = useAuth();

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center">
        <div className="w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!role || !allowedRoles.includes(role)) {
    if (fallback) return <>{fallback}</>;

    return (
      <div className="p-8 bg-white rounded-lg border border-brand-border text-center max-w-lg mx-auto my-8 shadow-sm">
        <div className="w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center mx-auto mb-4">
          <ShieldX className="w-6 h-6 text-brand-red" />
        </div>
        <h3 className="text-lg font-bold text-brand-black mb-2">Access Restricted</h3>
        <p className="text-sm text-brand-muted mb-4">
          You do not have permission to access this module. Your current role is{' '}
          <span className="font-semibold capitalize text-brand-black">{role || 'Unspecified'}</span>.
        </p>
        <p className="text-xs text-brand-muted">
          Required roles: {allowedRoles.map((r) => r.toUpperCase()).join(' or ')}
        </p>
      </div>
    );
  }

  return <>{children}</>;
};
